import type {
  ApolloClient,
  MutationOptions,
  NormalizedCacheObject,
  OperationVariables,
  QueryOptions,
} from '@apollo/client';
import { isDegradedNetworkFailure, isLegacyNetworkFailure } from './transportFailure';
import { isOfflineQueuedError } from './isOfflineQueuedError';

/**
 * Plazo de UNA fase entera (spec 2026-09-08-fase2-encolada § 2 D1): un solo
 * `AbortController` cuyo `signal` viaja en `context.fetchOptions` de todas las
 * llamadas de la fase. Al vencer, todo lo que esté en vuelo aborta a la vez.
 * Mismo techo que el link (`OFFLINE_QUEUE_TIMEOUT_MS`): el backend corta su
 * transacción a los 20 s; 25 s deja margen a una respuesta lenta pero viva.
 *
 * Por qué un plazo para toda la fase y no un techo por mutation: la fase 2 de
 * la actuación hace entre diez y veinte llamadas; con un techo por llamada la
 * espera sumaría minutos, y las lecturas (`client.query`) no pasan por el techo
 * del link. Un `signal` compartido corta todo a la vez.
 */
export const PHASE2_DEADLINE_MS = 25_000;

export interface PhaseDeadline {
  signal: AbortSignal;
  context: { skipOfflineQueue: true; fetchOptions: { signal: AbortSignal } };
  expired: () => boolean;
  clear: () => void;
}

export function createPhaseDeadline(ms: number = PHASE2_DEADLINE_MS): PhaseDeadline {
  const controller = new AbortController();
  let expired = false;
  const timer = setTimeout(() => {
    expired = true;
    controller.abort();
  }, ms);
  return {
    signal: controller.signal,
    context: { skipOfflineQueue: true, fetchOptions: { signal: controller.signal } },
    expired: () => expired,
    clear: () => clearTimeout(timer),
  };
}

/**
 * Mezcla el contexto del plazo en unas opciones de `client.query`/`client.mutate`:
 * `skipOfflineQueue` para que el link ni encole por su cuenta ni ponga otro
 * techo, y el `signal` para que `HttpLink` aborte el `fetch` al vencer.
 */
export function withPhaseTransport<T extends { context?: Record<string, unknown> }>(
  options: T,
  deadline: PhaseDeadline,
): T {
  const previous = options.context ?? {};
  const previousFetch =
    (previous.fetchOptions as Record<string, unknown> | undefined) ?? {};
  return {
    ...options,
    context: {
      ...previous,
      skipOfflineQueue: true,
      fetchOptions: { ...previousFetch, signal: deadline.signal },
    },
  };
}

/**
 * ¿Fallo de transporte? El centinela del link, o —desenvolviendo el
 * ApolloError— lo mismo que el link considera red degradada: aborto, `Failed
 * to fetch`, 502/503/504, respuesta truncada. Nunca con `graphQLErrors`: eso es
 * una respuesta del servidor, es decir, negocio.
 */
export function isTransportFailure(error: unknown, depth = 0): boolean {
  if (isOfflineQueuedError(error)) return true;
  const e = error as {
    networkError?: unknown;
    graphQLErrors?: unknown[];
    name?: string;
    cause?: unknown;
  } | null;
  // Sin servidor (`mutateWithDeadline` ni la envió) o plazo vencido: transporte
  // por definición (spec 2026-10-09 § 9). Sin esto el croquis de una edición
  // encolada gastaba un intento por pasada con el servidor caído y a los 5
  // quedaba aparcada.
  if (e?.name === 'BackendUnreachableError' || e?.name === 'MutationDeadlineError') {
    return true;
  }
  if (Array.isArray(e?.graphQLErrors) && e.graphQLErrors.length > 0) return false;
  // Envuelto (p. ej. `UploadImage` → `CloudinaryUploadError`): manda la causa.
  if (depth < 3 && e?.cause && e.cause !== error && isTransportFailure(e.cause, depth + 1)) {
    return true;
  }
  if (e?.networkError) return isDegradedNetworkFailure(e.networkError);
  // Sin `networkError` el error no ha pasado por el transporte de Apollo. Un
  // `TypeError` a secas solo es red si lo dice su mensaje (fetch en crudo:
  // «Failed to fetch») o si no hay red; el resto son fallos del código, y
  // encolarlos disfrazaría un bug de red degradada (el replay lo repetiría).
  if ((error as { name?: string } | null)?.name === 'TypeError') {
    return isLegacyNetworkFailure(error);
  }
  return isDegradedNetworkFailure(error);
}

export type Phase2Entry = 'replay-offline' | 'queue-create' | 'queue-existing' | 'online';

/**
 * Decisión de entrada de la fase 2 (spec § 3.1).
 *
 * - En replay, sin red → lanzar para que la cola conserve la MISMA operación.
 * - Borrador marcado sin red (fase 1 sin crear) → encolar la creación entera.
 * - Ids ya creados y sin llegar al servidor → encolar contra esos ids (D2).
 * - Sin ids y sin marca: borrador corrupto; el guard de ids de la sección con
 *   red es quien lanza, con su mensaje de siempre.
 */
export function decidePhase2Entry(input: {
  draftIsOffline: boolean;
  hasExistingIds: boolean;
  isReplay: boolean;
  online: boolean;
  reachable: boolean;
}): Phase2Entry {
  if (input.isReplay) return input.online ? 'online' : 'replay-offline';
  if (input.draftIsOffline) return 'queue-create';
  if (!input.hasExistingIds) return 'online';
  if (!input.online || !input.reachable) return 'queue-existing';
  return 'online';
}

/**
 * Un cliente acotado a la fase: las mismas llamadas que `client.query` y
 * `client.mutate`, con el contexto del plazo puesto. Es lo que permite que
 * un tripwire por código fuente exija que NINGUNA llamada de la sección con
 * red use el cliente desnudo.
 */
export function phaseScopedClient(
  client: ApolloClient<NormalizedCacheObject>,
  deadline: PhaseDeadline,
) {
  return {
    query: <T = any, V extends OperationVariables = OperationVariables>(
      options: QueryOptions<V, T>,
    ) => client.query<T, V>(withPhaseTransport(options, deadline)),
    mutate: <T = any, V extends OperationVariables = OperationVariables>(
      options: MutationOptions<T, V>,
    ) => client.mutate<T, V>(withPhaseTransport(options, deadline)),
  };
}

const abortError = () =>
  Object.assign(new Error('Plazo de la fase vencido'), { name: 'AbortError' });

/**
 * Para lo que no pasa por Apollo (la subida del croquis a Hetzner): al vencer
 * el plazo rechaza con `AbortError`, que `isTransportFailure` reconoce. La
 * promesa de dentro sigue su curso; lo que llegue tarde lo resuelve el replay.
 */
export function raceWithDeadline<T>(promise: Promise<T>, deadline: PhaseDeadline): Promise<T> {
  if (deadline.signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    deadline.signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        deadline.signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        deadline.signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}
